import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { defaultCode } from '../../common/constants';
import { useParams, useNavigate } from 'react-router-dom';
import { getToken } from '../../common/utils';
import proxy from '../../api/axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import ProblemJudgingPanel from '../../components/admin/problem-form/ProblemJudgingPanel';
import ProblemFormChecklist from '../../components/admin/problem-form/ProblemFormChecklist';
import ProblemFormExecutionLimits from '../../components/admin/problem-form/ProblemFormExecutionLimits';
import ProblemFormPreview from '../../components/admin/problem-form/ProblemFormPreview';
import {
  PROBLEM_SECTIONS,
  TOPIC_SUGGESTIONS,
  PROBLEM_FORM_TABS,
} from '../../components/admin/problem-form/problemFormConstants';
import { DEFAULT_SIGNATURE, isArgsOutputMode, isFunctionTestCaseValid } from '../../common/functionModeTypes';
import {
  AdminPage,
  AdminPageHeader,
  AdminFormSection,
  ACCENT,
  adminFieldSx,
  difficultyColor,
  useAdminPageTheme,
} from './adminUi';

const FORM_ID = 'admin-problem-form';

const stripHtml = (html) =>
  String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const ProblemFormPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { border, surface, muted, isDark } = useAdminPageTheme();

  const [activeTab, setActiveTab] = useState('basics');
  const [title, setTitle] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState('easy');
  const [constraints, setConstraints] = useState('');
  const [inputFormat, setInputFormat] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [testCases, setTestCases] = useState([
    { input: '', output: '', explanation: '', isSample: true },
  ]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [level, setLevel] = useState(1);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [section, setSection] = useState('');
  const [topics, setTopics] = useState([]);
  const [topicInput, setTopicInput] = useState('');
  const [wrapperCode, setWrapperCode] = useState(defaultCode);
  const [mode, setMode] = useState('io');
  const [signature, setSignature] = useState({ ...DEFAULT_SIGNATURE });
  const [starterCode, setStarterCode] = useState({});
  const [starterCodeEdited, setStarterCodeEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAdvancedFormats, setShowAdvancedFormats] = useState(false);
  const [executionLimits, setExecutionLimits] = useState({});
  const [stubHint, setStubHint] = useState('');
  const [starterPreamble, setStarterPreamble] = useState({});
  const [starterOptions, setStarterOptions] = useState({ includeBuiltinStructs: true });

  useEffect(() => {
    if (!slug) return;
    const fetchProblemDetails = async () => {
      try {
        const { data: problem } = await proxy.get(`/problems/admin/${slug}`);
        setTitle(problem.title);
        setDescription(problem.description);
        setDifficulty(problem.difficulty);
        setConstraints(problem.constraints || '');
        setInputFormat(problem.inputFormat || '');
        setOutputFormat(problem.outputFormat || '');
        setTestCases(problem.testCases || [{ input: '', output: '', explanation: '', isSample: false }]);
        setWrapperCode(problem.wrapperCode || defaultCode);
        setMode(problem.mode || 'io');
        setSignature(problem.signature || { ...DEFAULT_SIGNATURE });
        setStarterCode(problem.starterCode || problem.wrapperCode || {});
        setStarterCodeEdited(false);
        setVideoLink(problem.videoLink || '');
        setLevel(problem.level ?? 1);
        setSection(problem.section || '');
        setTopics(problem.topics || []);
        setDisplayOrder(problem.displayOrder ?? 0);
        setExecutionLimits(problem.executionLimits || {});
        setStubHint(problem.stubHint || '');
        setStarterPreamble(problem.starterPreamble || {});
        setStarterOptions(problem.starterOptions || { includeBuiltinStructs: true });
        if (problem.inputFormat || problem.outputFormat) setShowAdvancedFormats(true);
      } catch (error) {
        toast.error('Error fetching problem details');
        console.error(error);
      }
    };
    fetchProblemDetails();
  }, [slug]);

  useEffect(() => {
    if (slug || !section) return;
    const fetchNextDisplayOrder = async () => {
      try {
        const encodedSection = encodeURIComponent(section);
        const encodedLevel = encodeURIComponent(level);
        const { data } = await proxy.get(
          `/problems/next-display-order/?section=${encodedSection}&level=${encodedLevel}`,
        );
        setDisplayOrder(data?.nextDisplayOrder || 0);
      } catch (err) {
        console.error('Error fetching next display order:', err);
      }
    };
    fetchNextDisplayOrder();
  }, [level, section, slug]);

  const testsValid = useMemo(() => {
    if (mode === 'function') {
      return (
        testCases.length > 0 &&
        testCases.every((tc) => isFunctionTestCaseValid(tc, signature))
      );
    }
    return testCases.some((tc) => String(tc.input || '').trim());
  }, [mode, testCases, signature]);

  const checklistItems = useMemo(
    () => [
      {
        id: 'title',
        label: 'Problem title',
        hint: 'Add a clear title',
        done: Boolean(title.trim()),
      },
      {
        id: 'description',
        label: 'Description',
        hint: 'Write the problem statement',
        done: Boolean(stripHtml(description)),
      },
      {
        id: 'section',
        label: 'Track section',
        hint: 'Choose where it lives in practice',
        done: Boolean(section),
      },
      {
        id: 'tests',
        label: mode === 'function' ? 'Function test cases' : 'IO test cases',
        hint: mode === 'function' ? 'Args + expected for each case' : 'At least one input case',
        done: testsValid,
      },
      ...(mode === 'function'
        ? [
            {
              id: 'signature',
              label: 'Method name',
              hint: 'e.g. twoSum',
              done: Boolean(signature?.methodName?.trim()),
            },
          ]
        : []),
    ],
    [title, description, section, testsValid, mode, signature],
  );

  const progress = Math.round(
    (checklistItems.filter((i) => i.done).length / checklistItems.length) * 100,
  );

  const tabIndex = PROBLEM_FORM_TABS.findIndex((t) => t.id === activeTab);
  const goNext = () => {
    if (tabIndex < PROBLEM_FORM_TABS.length - 1) {
      setActiveTab(PROBLEM_FORM_TABS[tabIndex + 1].id);
    }
  };
  const goPrev = () => {
    if (tabIndex > 0) setActiveTab(PROBLEM_FORM_TABS[tabIndex - 1].id);
  };

  const handleAddTopic = (value) => {
    const v = (value ?? topicInput).trim().toLowerCase();
    if (v && !topics.includes(v)) {
      setTopics([...topics, v]);
      setTopicInput('');
    }
  };

  const handleRemoveTopic = (topicToRemove) => {
    setTopics(topics.filter((topic) => topic !== topicToRemove));
  };

  const handleSubmitProblem = async () => {
    const problemData = {
      title,
      description,
      difficulty,
      constraints,
      inputFormat,
      outputFormat,
      testCases,
      mode,
      signature: mode === 'function' ? signature : undefined,
      videoLink,
      isPublished: false,
      level,
      section,
      topics,
      displayOrder,
      executionLimits,
      stubHint,
      starterPreamble,
      starterOptions,
    };

    if (mode === 'function') {
      if (starterCodeEdited) {
        problemData.starterCode = starterCode;
        problemData.wrapperCode = starterCode;
      }
    } else {
      problemData.wrapperCode = wrapperCode;
    }

    if (!title.trim() || !stripHtml(description)) {
      toast.error('Title and description are required');
      setActiveTab(!title.trim() ? 'basics' : 'statement');
      return;
    }
    if (!section) {
      toast.error('Choose a track section');
      setActiveTab('placement');
      return;
    }
    if (mode === 'function') {
      if (!signature?.methodName?.trim()) {
        toast.error('Function mode requires a method name');
        setActiveTab('tests');
        return;
      }
      if (!testsValid) {
        toast.error(
          isArgsOutputMode(signature)
            ? 'Each void/in-place test case needs argument values and expected post-call values for the asserted parameters'
            : 'Each function test case needs args and expected values',
        );
        setActiveTab('tests');
        return;
      }
    } else if (!testsValid) {
      toast.error('Add at least one test case with input');
      setActiveTab('tests');
      return;
    }

    try {
      setIsSaving(true);
      if (slug) {
        const { data } = await proxy.put(`/problems/${slug}`, problemData, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        toast.success('Problem updated');
        navigate(`/admin/problems/${data.slug}`);
      } else {
        const { data } = await proxy.post('/problems', problemData, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        toast.success('Problem created');
        navigate(`/admin/problems/${data.slug}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving problem');
    } finally {
      setIsSaving(false);
    }
  };

  const DifficultyChip = ({ value, label }) => {
    const selected = difficulty === value;
    const color = difficultyColor(value);
    return (
      <Chip
        label={label}
        onClick={() => setDifficulty(value)}
        sx={{
          fontWeight: 800,
          cursor: 'pointer',
          bgcolor: selected ? alpha(color, isDark ? 0.25 : 0.12) : 'transparent',
          color: selected ? color : muted,
          border: `1.5px solid ${selected ? color : border}`,
        }}
      />
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basics':
        return (
          <AdminFormSection
            title="Problem basics"
            description="Name the problem and tag it so students can discover it."
          >
            <TextField
              label="Problem title"
              fullWidth
              required
              placeholder="Two Sum"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ ...adminFieldSx, mb: 2 }}
            />

            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>Difficulty</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
              <DifficultyChip value="easy" label="Easy" />
              <DifficultyChip value="medium" label="Medium" />
              <DifficultyChip value="hard" label="Hard" />
            </Box>

            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>Topics</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {TOPIC_SUGGESTIONS.filter((t) => !topics.includes(t)).slice(0, 8).map((t) => (
                <Chip
                  key={t}
                  label={`+ ${t}`}
                  size="small"
                  onClick={() => handleAddTopic(t)}
                  sx={{ fontWeight: 700, cursor: 'pointer' }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField
                label="Add topic"
                size="small"
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTopic())}
                sx={{ ...adminFieldSx, flex: 1 }}
              />
              <Button variant="outlined" onClick={() => handleAddTopic()} sx={{ fontWeight: 700 }}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {topics.map((topic) => (
                <Chip
                  key={topic}
                  label={topic}
                  onDelete={() => handleRemoveTopic(topic)}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Box>
          </AdminFormSection>
        );

      case 'statement':
        return (
          <>
            <AdminFormSection title="Problem statement" description="What the student reads before coding.">
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>Description</Typography>
              <Box
                sx={{
                  '& .ck-editor__editable': {
                    minHeight: 220,
                    borderRadius: 1.5,
                  },
                }}
              >
                <CKEditor
                  editor={ClassicEditor}
                  data={description}
                  onChange={(_, editor) => setDescription(editor.getData())}
                />
              </Box>
            </AdminFormSection>

            <AdminFormSection title="Constraints" description="Limits on input size, value ranges, etc.">
              <Box
                sx={{
                  '& .ck-editor__editable': {
                    minHeight: 120,
                    borderRadius: 1.5,
                  },
                }}
              >
                <CKEditor
                  editor={ClassicEditor}
                  data={constraints}
                  onChange={(_, editor) => setConstraints(editor.getData())}
                />
              </Box>
            </AdminFormSection>

            <AdminFormSection
              title="Input / output format"
              description="Optional for function mode. Useful for IO problems and contest statements."
            >
              {!showAdvancedFormats ? (
                <Button
                  variant="outlined"
                  onClick={() => setShowAdvancedFormats(true)}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Add input & output format fields
                </Button>
              ) : (
                <>
                  <TextField
                    label="Input format"
                    fullWidth
                    multiline
                    minRows={3}
                    value={inputFormat}
                    onChange={(e) => setInputFormat(e.target.value)}
                    sx={{ ...adminFieldSx, mb: 2 }}
                  />
                  <TextField
                    label="Output format"
                    fullWidth
                    multiline
                    minRows={3}
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    sx={adminFieldSx}
                  />
                </>
              )}
            </AdminFormSection>
          </>
        );

      case 'tests':
        return (
          <ProblemJudgingPanel
            slug={slug}
            mode={mode}
            onModeChange={setMode}
            signature={signature}
            onSignatureChange={setSignature}
            testCases={testCases}
            onTestCasesChange={setTestCases}
            starterCode={starterCode}
            onStarterCodeChange={setStarterCode}
            onStarterCodeEdited={() => setStarterCodeEdited(true)}
            onStarterCodeReset={() => setStarterCodeEdited(false)}
            wrapperCode={wrapperCode}
            onWrapperCodeChange={setWrapperCode}
            stubHint={stubHint}
            onStubHintChange={setStubHint}
            starterPreamble={starterPreamble}
            onStarterPreambleChange={setStarterPreamble}
            starterOptions={starterOptions}
            onStarterOptionsChange={setStarterOptions}
            onRegenerateStarters={() => {
              setStarterCode({});
              setStarterCodeEdited(false);
              toast.info('Starter overrides cleared — save to regenerate from signature.');
            }}
          />
        );

      case 'placement':
        return (
          <>
            <AdminFormSection
              title="Practice track placement"
              description="Where this problem appears in the learning path."
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>Section</Typography>
                  <Select
                    fullWidth
                    displayEmpty
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                    sx={adminFieldSx}
                    renderValue={(v) => v || 'Select section…'}
                  >
                    {PROBLEM_SECTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>Level</Typography>
                  <Select
                    fullWidth
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    sx={adminFieldSx}
                  >
                    <MenuItem value={1}>Level 1</MenuItem>
                    <MenuItem value={2}>Level 2</MenuItem>
                  </Select>
                </Box>
              </Box>

              <TextField
                label="Display order"
                type="number"
                fullWidth
                helperText="Order within the section (auto-suggested for new problems)"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                sx={{ ...adminFieldSx, mt: 2 }}
              />
            </AdminFormSection>

            <AdminFormSection
              title="Video walkthrough"
              description="Optional link to an explanation video."
            >
              <TextField
                label="Video URL"
                fullWidth
                placeholder="https://youtube.com/…"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                sx={adminFieldSx}
              />
            </AdminFormSection>

            <ProblemFormExecutionLimits
              limits={executionLimits}
              difficulty={difficulty}
              onChange={setExecutionLimits}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <AdminPage maxWidth={1100}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/manage-problems')}
        sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700, color: muted }}
      >
        All problems
      </Button>

      <AdminPageHeader
        title={slug ? 'Edit problem' : 'Create problem'}
        subtitle="Step through each tab — save anytime as a draft."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => setPreviewOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
              disabled={isSaving}
              onClick={handleSubmitProblem}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: ACCENT,
                '&:hover': { bgcolor: '#0f5c5e' },
              }}
            >
              {slug ? 'Update draft' : 'Save draft'}
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          mb: 2,
          p: 0.5,
          borderRadius: 2,
          border: `1px solid ${border}`,
          bgcolor: surface,
          overflowX: 'auto',
        }}
      >
        {PROBLEM_FORM_TABS.map((tab) => {
          const done =
            tab.id === 'basics'
              ? Boolean(title.trim()) && topics.length >= 0
              : tab.id === 'statement'
                ? Boolean(stripHtml(description))
                : tab.id === 'tests'
                  ? testsValid && (mode !== 'function' || signature?.methodName?.trim())
                  : Boolean(section);
          return (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                flex: { xs: '0 0 auto', sm: 1 },
                minWidth: 120,
                py: 1.25,
                px: 1.5,
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: activeTab === tab.id ? 800 : 600,
                color: activeTab === tab.id ? ACCENT : muted,
                bgcolor: activeTab === tab.id ? alpha(ACCENT, isDark ? 0.15 : 0.08) : 'transparent',
                borderBottom: activeTab === tab.id ? `2px solid ${ACCENT}` : '2px solid transparent',
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 'inherit', lineHeight: 1.2 }}>
                  {tab.label}
                  {done ? ' ✓' : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: muted, display: { xs: 'none', md: 'block' } }}>
                  {tab.hint}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 280px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box id={FORM_ID}>{renderTabContent()}</Box>
        <ProblemFormChecklist items={checklistItems} progress={progress} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${border}`,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={tabIndex === 0}
          onClick={goPrev}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Previous
        </Button>
        {tabIndex < PROBLEM_FORM_TABS.length - 1 ? (
          <Button
            endIcon={<ArrowForwardIcon />}
            variant="contained"
            onClick={goNext}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            Next: {PROBLEM_FORM_TABS[tabIndex + 1].label}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            disabled={isSaving}
            onClick={handleSubmitProblem}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            Save draft
          </Button>
        )}
      </Box>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>Student preview</Typography>
            <Typography variant="caption" sx={{ color: muted }}>
              How this problem will look before you publish
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setPreviewOpen(false)} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <ProblemFormPreview
            title={title}
            description={description}
            difficulty={difficulty}
            constraints={constraints}
            inputFormat={inputFormat}
            outputFormat={outputFormat}
            topics={topics}
            section={section}
            level={level}
            mode={mode}
            signature={signature}
            testCases={testCases}
            stubHint={stubHint}
            starterCode={starterCode}
            executionLimits={executionLimits}
            starterOptions={starterOptions}
          />
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default ProblemFormPage;
